import React from 'react'
import cx from 'classnames'
import isFunction from 'lodash/isFunction'
import PropTypes from 'prop-types'

import { DescriptionIcon } from '@wf-sl/common-icons'
import T3Constants from '../constants'
import { FilterableList } from '../filterable-list'
import Tooltip from '../tooltip/tooltip'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import IconSelect from './icon-select'
import Select from './select'

import styles from './select.module.css'

const customFilter = (value, child) => {
    const filter = value.trim().toLowerCase()
    const children =
        child.props.children && isFunction(child.props.children.toLowerCase) ? child.props.children.toLowerCase() : ''
    const text = child.props.affix ? `${children} ${child.props.affix.toLowerCase()}` : children
    return filter === '' || (child.props.selectable && text.includes(filter))
}

const customHighlight = (value, child) => {
    const filter = value.trim().toLowerCase()
    const childrenToString =
        child.props.children && isFunction(child.props.children.toString) ? child.props.children.toString() : ''

    const tooltip = child.props.tooltip ? (
        <Tooltip content={child.props.tooltip} placement="right">
            <div className={styles['icon-hint']}>
                <DescriptionIcon />
            </div>
        </Tooltip>
    ) : null

    const affix = child.props.affix ? (
        <div className={styles['affix']}>{` ${T3Constants.HtmlEntities.NDASH} ${child.props.affix.toString()}`}</div>
    ) : null

    const listItemClass = tooltip ? 'list-item-with-tooltip' : 'list-item'

    // If a filter or affix is set, all contained <Option /> display strings will be converted into a React Component.
    if (filter !== '') {
        const childValue = childrenToString
        const affix = child.props.affix ? child.props.affix : ''
        const compound = `${childValue} ${affix}`
        const index = compound.trim().toLowerCase().indexOf(filter)
        const affixSpan = child.props.affix ? (
            <span className={styles['affix']}>
                {` ${child.props.affix ? T3Constants.HtmlEntities.NDASH : ''} `}
                {compound.substring(childValue.length, Math.max(childValue.length, index))}
                <u>
                    <strong>
                        {compound.substring(
                            Math.max(childValue.length + 1, index),
                            Math.max(childValue.length + 1, index + filter.length)
                        )}
                    </strong>
                </u>
                {compound.substring(Math.max(childValue.length + 1, index + filter.length), compound.length)}
            </span>
        ) : (
            ''
        )

        const newValue = (
            <span className={styles[listItemClass]}>
                {compound.substring(0, Math.min(index, childValue.length))}
                <u>
                    <strong>
                        {compound.substring(
                            Math.min(index, childValue.length),
                            Math.min(childValue.length, index + filter.length)
                        )}
                    </strong>
                </u>
                {compound.substring(Math.min(childValue.length, index + filter.length), childValue.length)}
                {affixSpan}
                {tooltip}
            </span>
        )
        // The contained <Option /> derives the title hint from the options display string. But in case of a filter or
        // affix, the display string is converted here into a React component, and the title dirivation does not work.
        // So if the <Option /> doesn't own an explicit ttile yet, we 'save' the display string as an explicit title.
        child = React.cloneElement(child, { title: child.props.title ? child.props.title : childrenToString }, newValue)
    } else if (affix || tooltip) {
        const newValue = (
            <span className={styles[listItemClass]}>
                {childrenToString}
                {affix}
                {tooltip}
            </span>
        )
        child = React.cloneElement(child, { title: child.props.title ? child.props.title : childrenToString }, newValue)
    }
    return child
}

const propTypesList = {
    filterPlaceholder: PropTypes.string,
    open: PropTypes.bool,
    optionListClassName: PropTypes.string,
    virtual: virtualListPropType
}

const propTypesListFilteredSelect = {
    filterPlaceholder: PropTypes.string,
    headerClassName: PropTypes.string,
    icon: PropTypes.node,
    noResultNode: PropTypes.node,
    optionListClassName: PropTypes.string,
    virtual: virtualListPropType
}

function FilterableOptionList({ children, filterPlaceholder, open, optionListClassName, virtual, ...props }) {
    const className = cx(styles['option-list'], {
        [styles['list-open']]: open
    })
    const listClassName = cx(
        't3sel-filterable-dropdown-list',
        styles['options-list-items'],
        {
            [styles['virtual-list']]: virtual
        },
        optionListClassName
    )
    return (
        <div className={className}>
            <FilterableList
                filter={customFilter}
                highlight={customHighlight}
                className={listClassName}
                placeholder={filterPlaceholder}
                {...{ virtual }}
                {...props}
            >
                {children}
            </FilterableList>
        </div>
    )
}

FilterableOptionList.propTypes = propTypesList
FilteredSelect.propTypes = propTypesListFilteredSelect

function FilteredSelect(props) {
    const { children, filterPlaceholder, icon, noResultNode, optionListClassName, virtual, ...restProps } = props

    const listProps = { children, filterPlaceholder, icon, noResultNode, optionListClassName, virtual }
    const filteredList = <FilterableOptionList {...listProps} />

    if (icon !== undefined && icon !== null) {
        return (
            <IconSelect icon={icon} list={filteredList} {...restProps}>
                {children}
            </IconSelect>
        )
    } else {
        return (
            <Select list={filteredList} {...props}>
                {children}
            </Select>
        )
    }
}

export { FilteredSelect, FilterableOptionList }
